import { Population } from '../types/iloe';
import { Community } from '../types/community';
import { TeamColor } from '../types/wargame-teams';
import { SECTOR_POSITIONS } from '../data/sectors';

export interface NodePosition {
  x: number;
  y: number;
}

export interface NetworkLayout {
  nodes: Record<string, NodePosition>;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

// Color mapping for teams
export const TEAM_COLORS: Record<TeamColor | 'Neutral', string> = {
  'Blue': '#3b82f6', // blue-500
  'Red': '#ef4444', // red-500
  'Yellow': '#eab308', // yellow-500
  'Green': '#22c55e', // green-500
  'Pink': '#ec4899', // pink-500
  'Black': '#1f2937', // gray-800
  'White': '#f3f4f6', // gray-100
  'Neutral': '#9ca3af', // gray-400
};

// Color mapping for sectors (for background/grouping)
export const SECTOR_COLORS: Record<string, string> = {
  'S001': '#1e3a8a', // Defense (Dark Blue)
  'S002': '#065f46', // Finance (Dark Green)
  'S003': '#991b1b', // Healthcare (Dark Red)
  'S004': '#92400e', // Transportation (Dark Orange)
  'S005': '#5b21b6', // Education (Dark Purple)
  'S006': '#854d0e', // Public Safety (Dark Yellow)
  'S007': '#374151', // Manufacturing (Dark Gray)
  'S008': '#3f6212', // Agriculture (Dark Lime)
  'S009': '#4c1d95', // Leadership (Dark Violet)
};

/**
 * Generates a hierarchical layout for the network.
 * Sectors -> Communities -> Populations
 */
export const generateNetworkLayout = (
  communities: Community[], 
  populations: Population[],
  width: number,
  height: number
): NetworkLayout => {
  const nodes: Record<string, NodePosition> = {};
  const sectorCenters: Record<string, NodePosition> = {};

  // 1. Map Sector Positions (from percentage strings to pixels)
  Object.entries(SECTOR_POSITIONS).forEach(([id, pos]) => {
    const x = (parseFloat(pos.left) / 100) * width;
    const y = (parseFloat(pos.top) / 100) * height;
    sectorCenters[id] = { x, y };
  });

  // 2. Place Communities around Sectors
  const communitiesBySector: Record<string, Community[]> = {};
  communities.forEach(c => {
    if (!communitiesBySector[c.sectorId]) communitiesBySector[c.sectorId] = [];
    communitiesBySector[c.sectorId].push(c);
  });

  Object.entries(communitiesBySector).forEach(([sectorId, comms]) => {
    const center = sectorCenters[sectorId] || { x: width / 2, y: height / 2 };
    const radius = 150; // Radius for communities ring
    
    comms.forEach((comm, index) => {
      const angle = (index / comms.length) * 2 * Math.PI;
      const cx = center.x + Math.cos(angle) * radius;
      const cy = center.y + Math.sin(angle) * radius;
      
      nodes[comm.id] = { x: cx, y: cy };

      // 3. Place Populations around Communities
      const commPops = populations.filter(p => p.communityId === comm.id);
      const popRadius = 40; // Radius for population ring around community

      commPops.forEach((pop, pIndex) => {
        const pAngle = (pIndex / commPops.length) * 2 * Math.PI;
        nodes[pop.id] = {
          x: cx + Math.cos(pAngle) * popRadius,
          y: cy + Math.sin(pAngle) * popRadius
        };
      });
    });
  });

  return {
    nodes,
    bounds: { minX: 0, minY: 0, maxX: width, maxY: height }
  };
};

export const calculateNodeColor = (
  belief: TeamColor | null, 
  behavior: TeamColor | null
): string => {
  if (behavior) return TEAM_COLORS[behavior];
  if (belief) return TEAM_COLORS[belief]; // Lighter version?
  return TEAM_COLORS['Neutral'];
};

export const formatPopulationLabel = (pop: Population): string => {
  return `${pop.name} (Limit: ${pop.evaluativeLimit})`;
};

export const getClusterBounds = (
  communityId: string, 
  layout: NetworkLayout, 
  populations: Population[]
): { x: number, y: number, r: number } | null => {
  const commNode = layout.nodes[communityId];
  if (!commNode) return null;

  // Simple bounding circle
  return { x: commNode.x, y: commNode.y, r: 60 };
};
